/*
redactionProvider.tsx
Copyright © 2026 Xerox Corporation. All Rights Reserved.

Copyright protection claimed includes all forms and matters of copyrightable material
and information now allowed by statutory or judicial law or hereinafter granted,
including without limitation, material generated from the software programs which
are displayed on the screen such as icons, screen display looks, etc.
*/

import { ReactNode, useState, createContext, useContext } from 'react'
import { RedactProfile } from '../modules/redactProfile'
import { RedactionStatus } from '../types/redactionStatus'
import { defaultRedactionStatus } from '../constants/defaultRedactionStatus'

interface redactionContextProviderProps {
  children: ReactNode
}
interface redactionContextProps {
  selectedRedactProfile: RedactProfile|undefined
  setSelectedRedactProfile: React.Dispatch<React.SetStateAction<RedactProfile|undefined>>
  redactProfiles: RedactProfile[],
  setRedactProfiles: React.Dispatch<React.SetStateAction<RedactProfile[]>>,
  redactionStatus: RedactionStatus,
  setRedactionStatus: React.Dispatch<React.SetStateAction<RedactionStatus>>
  draftRedactProfile: RedactProfile|undefined
  setDraftRedactProfile: React.Dispatch<React.SetStateAction<RedactProfile|undefined>>
  resetDraftRedactProfile: () => void,
  redactedDocPreview: string[],
  setRedactedDocPreview: React.Dispatch<React.SetStateAction<string[]>>
}
const redactionContext = createContext<redactionContextProps | null>(null)

export const RedactionProvider = (props: redactionContextProviderProps) => {
  const [selectedRedactProfile, setSelectedRedactProfile] = useState<RedactProfile|undefined>(undefined);
   const [redactProfiles, setRedactProfiles] = useState<RedactProfile[]>([]);
   const [redactionStatus, setRedactionStatus] = useState<RedactionStatus>(defaultRedactionStatus);
   const [draftRedactProfile, setDraftRedactProfile] = useState<RedactProfile|undefined>(undefined);
   const [redactedDocPreview, setRedactedDocPreview] = useState<string[]>([]);
   const resetDraftRedactProfile = () => {
    setDraftRedactProfile(selectedRedactProfile);
   }
  const contextElements = {
    selectedRedactProfile,
    setSelectedRedactProfile,
    redactProfiles,
    setRedactProfiles,
    redactionStatus,
    setRedactionStatus,
    draftRedactProfile,
    setDraftRedactProfile,
    resetDraftRedactProfile,
    redactedDocPreview,
    setRedactedDocPreview
  }
  return (
    <redactionContext.Provider value={contextElements}>
      {props.children}
    </redactionContext.Provider>
  )
}

export const useRedactionContext = () => {
  const context = useContext(redactionContext)
  if (!context) {
    throw new Error(
      'useRedactionContext must be used within a RedactionContextProvider'
    )
  }
  return context
}
export default RedactionProvider